import React, { useState, useEffect, useCallback } from 'react';
import { Download, Upload, RefreshCw, Search, FileText, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatBytes = (b) => {
  if (!b || b <= 0) return '-';
  if (b < 1024) return `${b} o`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} Ko`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} Mo`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} Go`;
};

const formatDuration = (ms) => {
  if (!ms || ms <= 0) return '-';
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s % 60)}s`;
};

const formatSpeed = (bps) => {
  if (!bps || bps <= 0) return '-';
  if (bps < 1024 * 1024) return `${(bps / 1024).toFixed(0)} Ko/s`;
  return `${(bps / (1024 * 1024)).toFixed(1)} Mo/s`;
};

const eventLabel = (e) => ({
  upload_start: 'Upload démarré',
  upload_success: 'Upload réussi',
  upload_failed: 'Upload échoué',
  download_success: 'Téléchargement',
  download_failed: 'Téléchargement échoué',
}[e] || e);

const statusBadge = (status) => {
  const cls = {
    success: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    failed: 'bg-red-500/20 text-red-300 border-red-500/30',
    in_progress: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  }[status] || 'bg-slate-700/40 text-slate-300 border-slate-600/30';
  return <span className={`px-2 py-0.5 rounded text-xs border ${cls}`}>{status}</span>;
};

const AdminTransfersTab = ({ token, toast }) => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const limit = 50;

  // Filters
  const [eventFilter, setEventFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [minSize, setMinSize] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (eventFilter) params.event = eventFilter;
      if (statusFilter) params.status = statusFilter;
      if (userFilter) params.user_email = userFilter;
      if (minSize) params.min_size = parseInt(minSize) * 1024 * 1024;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await axios.get(`${API}/admin/transfer-logs`, {
        headers: { Authorization: `Bearer ${token}` }, params,
      });
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Impossible de charger les logs', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [token, page, eventFilter, statusFilter, userFilter, minSize, dateFrom, dateTo, toast]);

  const loadStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/transfer-logs/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(res.data);
    } catch {}
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const exportCsv = async () => {
    try {
      const params = {};
      if (eventFilter) params.event = eventFilter;
      if (statusFilter) params.status = statusFilter;
      const res = await axios.get(`${API}/admin/transfer-logs/export`, {
        headers: { Authorization: `Bearer ${token}` }, params, responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sendbox_transfers_${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Erreur', description: 'Export impossible', variant: 'destructive' });
    }
  };

  const resetFilters = () => {
    setEventFilter(''); setStatusFilter(''); setUserFilter('');
    setMinSize(''); setDateFrom(''); setDateTo(''); setPage(1);
  };

  const totalPages = Math.ceil(total / limit) || 1;

  return (
    <div className="space-y-4" data-testid="transfers-tab-content">
      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-slate-800/40 border border-emerald-500/20 rounded-lg p-3" data-testid="stat-upload-success">
            <div className="flex items-center gap-2 text-emerald-300 text-xs">
              <Upload className="w-3.5 h-3.5" /> Uploads réussis
            </div>
            <div className="text-2xl font-bold text-white mt-1">{stats.upload_success}</div>
            <div className="text-xs text-blue-300/50 mt-0.5">{formatBytes(stats.total_upload_bytes)}</div>
          </div>
          <div className="bg-slate-800/40 border border-red-500/20 rounded-lg p-3" data-testid="stat-upload-failed">
            <div className="flex items-center gap-2 text-red-300 text-xs">
              <AlertCircle className="w-3.5 h-3.5" /> Uploads échoués
            </div>
            <div className="text-2xl font-bold text-white mt-1">{stats.upload_failed}</div>
          </div>
          <div className="bg-slate-800/40 border border-emerald-500/20 rounded-lg p-3" data-testid="stat-download-success">
            <div className="flex items-center gap-2 text-emerald-300 text-xs">
              <Download className="w-3.5 h-3.5" /> Téléchargements
            </div>
            <div className="text-2xl font-bold text-white mt-1">{stats.download_success}</div>
            <div className="text-xs text-blue-300/50 mt-0.5">{formatBytes(stats.total_download_bytes)}</div>
          </div>
          <div className="bg-slate-800/40 border border-red-500/20 rounded-lg p-3" data-testid="stat-download-failed">
            <div className="flex items-center gap-2 text-red-300 text-xs">
              <AlertCircle className="w-3.5 h-3.5" /> DL échoués
            </div>
            <div className="text-2xl font-bold text-white mt-1">{stats.download_failed}</div>
          </div>
          <div className="bg-slate-800/40 border border-amber-500/20 rounded-lg p-3" data-testid="stat-in-progress">
            <div className="flex items-center gap-2 text-amber-300 text-xs">
              <Clock className="w-3.5 h-3.5" /> En cours
            </div>
            <div className="text-2xl font-bold text-white mt-1">{stats.in_progress}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-slate-800/40 border border-blue-500/20 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-blue-200 text-sm font-semibold flex items-center gap-2">
            <Search className="w-4 h-4" /> Filtres
          </h3>
          <div className="flex gap-2">
            <Button onClick={resetFilters} variant="outline" size="sm" className="border-blue-500/30 text-blue-300 hover:bg-blue-500/10 text-xs" data-testid="reset-filters-btn">
              Réinitialiser
            </Button>
            <Button onClick={exportCsv} variant="outline" size="sm" className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 text-xs" data-testid="export-csv-btn">
              <FileText className="w-3.5 h-3.5 mr-1" /> Export CSV
            </Button>
            <Button onClick={load} variant="outline" size="sm" className="border-blue-500/30 text-blue-300 hover:bg-blue-500/10 text-xs" data-testid="refresh-logs-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Actualiser
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          <select value={eventFilter} onChange={(e) => { setEventFilter(e.target.value); setPage(1); }} className="bg-slate-900/60 border border-blue-500/20 text-blue-200 rounded px-2 py-1.5 text-sm" data-testid="event-filter">
            <option value="">Tous les événements</option>
            <option value="upload_start">Upload démarré</option>
            <option value="upload_success">Upload réussi</option>
            <option value="upload_failed">Upload échoué</option>
            <option value="download_success">Téléchargement</option>
            <option value="download_failed">DL échoué</option>
          </select>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="bg-slate-900/60 border border-blue-500/20 text-blue-200 rounded px-2 py-1.5 text-sm" data-testid="status-filter">
            <option value="">Tous les statuts</option>
            <option value="success">Succès</option>
            <option value="failed">Échec</option>
            <option value="in_progress">En cours</option>
          </select>
          <input type="text" placeholder="Email utilisateur..." value={userFilter} onChange={(e) => { setUserFilter(e.target.value); setPage(1); }} className="bg-slate-900/60 border border-blue-500/20 text-blue-200 rounded px-2 py-1.5 text-sm placeholder-blue-300/40" data-testid="user-filter" />
          <input type="number" placeholder="Taille min (Mo)" value={minSize} onChange={(e) => { setMinSize(e.target.value); setPage(1); }} className="bg-slate-900/60 border border-blue-500/20 text-blue-200 rounded px-2 py-1.5 text-sm placeholder-blue-300/40" data-testid="min-size-filter" />
          <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} className="bg-slate-900/60 border border-blue-500/20 text-blue-200 rounded px-2 py-1.5 text-sm" data-testid="date-from-filter" />
          <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} className="bg-slate-900/60 border border-blue-500/20 text-blue-200 rounded px-2 py-1.5 text-sm" data-testid="date-to-filter" />
        </div>
      </div>

      {/* Logs table */}
      <div className="bg-slate-800/40 border border-blue-500/20 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/60 border-b border-blue-500/20">
              <tr className="text-left text-blue-200 text-xs uppercase">
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Événement</th>
                <th className="px-3 py-2">Statut</th>
                <th className="px-3 py-2">Fichier</th>
                <th className="px-3 py-2">Taille</th>
                <th className="px-3 py-2">Vitesse</th>
                <th className="px-3 py-2">Durée</th>
                <th className="px-3 py-2">Utilisateur</th>
                <th className="px-3 py-2">IP</th>
                <th className="px-3 py-2">Mode</th>
                <th className="px-3 py-2">Share</th>
                <th className="px-3 py-2">Erreur</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={12} className="text-center text-blue-300/60 py-6">Chargement...</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={12} className="text-center text-blue-300/60 py-6">Aucun log trouvé</td></tr>
              ) : logs.map((log, idx) => (
                <tr key={idx} className="border-b border-blue-500/10 text-blue-100 hover:bg-slate-900/40">
                  <td className="px-3 py-2 text-xs text-blue-300/70 whitespace-nowrap">{new Date(log.created_at).toLocaleString('fr-FR')}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{eventLabel(log.event)}</td>
                  <td className="px-3 py-2">{statusBadge(log.status)}</td>
                  <td className="px-3 py-2 text-xs max-w-xs truncate" title={log.filename}>{log.filename || '-'}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{formatBytes(log.file_size)}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{formatSpeed(log.avg_speed_bps)}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{formatDuration(log.duration_ms)}</td>
                  <td className="px-3 py-2 text-xs max-w-[150px] truncate" title={log.user_email}>{log.user_email || <span className="text-blue-300/40">Anonyme</span>}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{log.ip || '-'}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{log.storage_mode || '-'}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap font-mono">{log.share_id || '-'}</td>
                  <td className="px-3 py-2 text-xs text-red-300 max-w-[200px] truncate" title={log.error}>{log.error || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-blue-500/20 text-sm">
            <span className="text-blue-300/70">Page {page} sur {totalPages} ({total} entrées au total)</span>
            <div className="flex gap-2">
              <Button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} variant="outline" size="sm" className="border-blue-500/30 text-blue-300 disabled:opacity-30" data-testid="prev-page-btn">
                Précédent
              </Button>
              <Button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} variant="outline" size="sm" className="border-blue-500/30 text-blue-300 disabled:opacity-30" data-testid="next-page-btn">
                Suivant
              </Button>
            </div>
          </div>
        )}
        <div className="px-4 py-2 border-t border-blue-500/10 text-xs text-blue-300/40 flex items-center gap-2">
          <CheckCircle2 className="w-3 h-3" /> Les logs sont conservés pendant 7 jours puis supprimés automatiquement
        </div>
      </div>
    </div>
  );
};

export default AdminTransfersTab;
